import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Shield, X } from "lucide-react";

// State-specific statutory disclosures. The wording paraphrases each
// statute's mandatory disclosure points (purpose, retention schedule,
// third-party sharing) — NOT legal advice. The user must explicitly
// acknowledge before the standard consent checkbox becomes available.
const DISCLOSURES = {
  IL: {
    title: "Illinois Biometric Information Privacy Act (BIPA) notice",
    statute: "740 ILCS 14/15(b)",
    bullets: [
      "We are collecting and analyzing your facial geometry from the photo you upload.",
      "The specific purpose is to identify the haircut you are showing us and recommend matching hair products and a barber script.",
      "Our retention schedule: your photo and any derived facial-geometry data are deleted from our storage IMMEDIATELY after the analysis completes. We do not retain biometric identifiers or biometric information beyond the duration of the single analysis request.",
      "We will never sell, lease, trade, or otherwise profit from your biometric identifiers or biometric information.",
      "We will not disclose your biometric identifiers or biometric information to any third party except (a) the AI vision provider that performs the single analysis under a zero-retention contractual obligation, or (b) as required by law.",
    ],
  },
  TX: {
    title: "Texas Capture or Use of Biometric Identifier (CUBI) Act notice",
    statute: "Tex. Bus. & Com. Code §503.001",
    bullets: [
      "We are capturing a biometric identifier — facial geometry — from the photo you submit.",
      "The purpose of the capture is to recommend hairstyles and grooming products that match the cut shown.",
      "We will not sell, lease, or otherwise disclose your biometric identifier to a third party for any purpose, except to the AI vision provider that performs the analysis under a zero-retention obligation, or as required by law.",
      "We destroy the biometric identifier within a reasonable time, and in all cases within one year of the purpose being satisfied — in practice, immediately after analysis completes.",
    ],
  },
  WA: {
    title: "Washington Biometric Privacy notice",
    statute: "RCW 19.375.020",
    bullets: [
      "We are enrolling a biometric identifier (facial geometry) from your photo in our service for the purpose of generating hairstyle recommendations.",
      "We will not sell, lease, or disclose your biometric identifier to any third party for a commercial purpose without your separate, specific consent.",
      "Retention: the photo and any derived facial-geometry data are deleted immediately after the analysis completes. We do not store biometric identifiers beyond the single analysis request.",
      "You may withdraw consent at any time by deleting your account from the Account page.",
    ],
  },
};

export default function BiometricDisclosureModal({ state, open, onAcknowledge, onClose }) {
  const acceptBtnRef = useRef(null);
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    // Focus the primary action on open + Escape closes
    setTimeout(() => acceptBtnRef.current?.focus(), 50);
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open || !state) return null;
  const disc = DISCLOSURES[state];
  if (!disc) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center px-4 py-6 bg-black/85 backdrop-blur-sm animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-labelledby="bipa-modal-title"
      aria-describedby="bipa-modal-body"
      data-testid="bipa-modal"
      ref={dialogRef}
    >
      <div className="relative w-full max-w-2xl max-h-[88vh] overflow-y-auto cm-card p-7 sm:p-9 border-copper/40">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close disclosure"
          className="absolute top-4 right-4 w-9 h-9 inline-flex items-center justify-center rounded-md border border-ink-700 text-platinum hover:border-copper transition-colors"
          data-testid="bipa-modal-close"
        >
          <X className="w-4 h-4" aria-hidden="true" />
        </button>

        <div className="flex items-start gap-3">
          <Shield className="w-6 h-6 text-copper mt-1 flex-shrink-0" aria-hidden="true" />
          <div>
            <p className="cm-label text-[10px] text-copper">STATE-SPECIFIC DISCLOSURE</p>
            <h2 id="bipa-modal-title" className="cm-heading text-2xl sm:text-3xl text-platinum mt-2">
              {disc.title}
            </h2>
            <p className="mt-1 text-xs text-gray-500 font-mono">{disc.statute}</p>
          </div>
        </div>

        <p id="bipa-modal-body" className="mt-6 text-sm text-gray-300 leading-relaxed">
          Because you appear to be in {state === "IL" ? "Illinois" : state === "TX" ? "Texas" : "Washington"},
          state law requires us to disclose the following before we collect or
          analyze your facial geometry. Please read in full before continuing.
        </p>

        <ul className="mt-5 space-y-3 text-sm text-gray-300 leading-relaxed list-disc list-inside marker:text-copper">
          {disc.bullets.map((b, i) => (
            <li key={i}>{b}</li>
          ))}
        </ul>

        <p className="mt-5 text-xs text-gray-500 leading-relaxed">
          For full detail, please review our{" "}
          <Link to="/privacy" className="text-copper underline hover:text-copper/80">
            Privacy Policy
          </Link>
          . Acknowledging below also constitutes the written release required
          by the applicable statute.
        </p>

        <div className="mt-7 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="cm-btn-secondary"
            data-testid="bipa-modal-cancel"
          >
            Not now
          </button>
          <button
            type="button"
            ref={acceptBtnRef}
            onClick={() => onAcknowledge?.(disc.statute)}
            className="cm-btn-primary inline-flex items-center justify-center gap-2"
            data-testid="bipa-modal-accept"
          >
            I acknowledge &amp; agree
          </button>
        </div>
      </div>
    </div>
  );
}
